// frontend/src/modules/payroll/components/TaxRulesPanel.tsx
//
// Tenant-level percentage withholding rules. Active rules become deduction
// lines on every computed run (annual caps respected via YTD withheld).
import * as React from 'react';
import { toast } from 'sonner';
import { Plus, Percent } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { useApi } from '../../../design-system/useApi';
import { formatMoney } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { payrollApi } from '../api';
import type { TaxRule, TaxAppliesTo } from '../types';

export function TaxRulesPanel({ currency }: { currency: string }) {
  const rulesQ = useApi<{ rules: TaxRule[] }>('/api/payroll/tax-rules');
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<TaxRule | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const rules = rulesQ.data?.rules ?? [];

  async function toggleActive(rule: TaxRule) {
    setBusyId(rule.id);
    try {
      await payrollApi.updateTaxRule(rule.id, { active: !rule.active });
      toast.success(rule.active ? 'Rule deactivated' : 'Rule reactivated');
      rulesQ.reload();
    } catch (err: any) {
      toast.error('Update failed', { description: err.message });
    } finally { setBusyId(null); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-muted-foreground max-w-xl">
          Percentage withholding applied at compute time — from gross pay or base pay,
          with an optional annual cap per employee. Simple flat-rate rules only (no brackets).
        </p>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5 shrink-0">
          <Plus className="h-3.5 w-3.5" /> Add tax rule
        </Button>
      </div>

      {rulesQ.loading ? (
        <div className={[VISUAL.bento, 'p-10 text-center text-[13px] text-muted-foreground'].join(' ')}>Loading…</div>
      ) : rules.length === 0 ? (
        <div className={[VISUAL.bento, 'py-14 px-8 text-center'].join(' ')}>
          <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-saffron/15 ring-1 ring-saffron/25 flex items-center justify-center">
            <Percent className="h-6 w-6 text-saffron-foreground" strokeWidth={1.6} />
          </div>
          <p className="text-[16px] font-semibold tracking-[-0.01em] text-foreground">No withholding rules</p>
          <p className="text-[13px] text-muted-foreground mt-1.5 max-w-sm mx-auto">
            Add a rule like "Federal withholding · 12% of gross" and every computed run will include it as a deduction.
          </p>
        </div>
      ) : (
        <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
          <ul className="divide-y divide-border/40">
            {rules.map((rule) => (
              <li key={rule.id} className="flex items-center gap-4 px-5 sm:px-6 py-4">
                <div className="h-10 w-10 rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 flex items-center justify-center shrink-0">
                  <Percent className="h-4 w-4 text-saffron-foreground" strokeWidth={1.7} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold tracking-[-0.005em] text-foreground truncate">{rule.name}</p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">
                    {Number(rule.rate_percent)}% of {rule.applies_to === 'base' ? 'base pay' : 'gross pay'}
                    {rule.annual_cap != null ? ` · capped at ${formatMoney(rule.annual_cap, currency)}/yr` : ''}
                  </p>
                </div>
                {rule.active
                  ? <span className={statusPill('success')}><span className={statusDot('success')} />Active</span>
                  : <span className={statusPill('muted')}><span className={statusDot('muted')} />Inactive</span>}
                <Button size="sm" variant="ghost" className="rounded-full h-8 text-[12px]"
                  onClick={() => { setEditing(rule); setDialogOpen(true); }}>
                  Edit
                </Button>
                <Button size="sm" variant="outline" disabled={busyId === rule.id} className="rounded-full h-8 text-[12px]"
                  onClick={() => toggleActive(rule)}>
                  {rule.active ? 'Deactivate' : 'Reactivate'}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <TaxRuleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={() => { setDialogOpen(false); rulesQ.reload(); }}
      />
    </div>
  );
}

function TaxRuleDialog({
  open, onOpenChange, editing, onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: TaxRule | null;
  onSaved: () => void;
}) {
  const [name, setName] = React.useState('');
  const [rate, setRate] = React.useState('');
  const [appliesTo, setAppliesTo] = React.useState<TaxAppliesTo>('gross');
  const [cap, setCap] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setRate(String(Number(editing.rate_percent)));
      setAppliesTo(editing.applies_to);
      setCap(editing.annual_cap != null ? String(Number(editing.annual_cap)) : '');
    } else {
      setName(''); setRate(''); setAppliesTo('gross'); setCap('');
    }
  }, [open, editing]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !rate) {
      toast.error('Name and rate are required');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(), rate_percent: Number(rate), applies_to: appliesTo,
        annual_cap: cap ? Number(cap) : null,
      };
      if (editing) {
        await payrollApi.updateTaxRule(editing.id, payload);
        toast.success('Tax rule updated');
      } else {
        await payrollApi.createTaxRule(payload);
        toast.success('Tax rule added');
      }
      onSaved();
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? 'Edit tax rule' : 'Add tax rule'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Federal withholding" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Rate (%)</Label>
              <Input type="number" min={0.01} max={100} step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Applies to</Label>
              <Select value={appliesTo} onValueChange={(v) => setAppliesTo(v as TaxAppliesTo)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gross">Gross pay</SelectItem>
                  <SelectItem value="base">Base pay</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Annual cap <span className="text-muted-foreground font-normal">(optional, per employee per year)</span></Label>
            <Input type="number" min={0} step="0.01" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="e.g. 9000" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : editing ? 'Save changes' : 'Add rule'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
